import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { QUERY_KEYS } from "../config";
import Translations from "@translate/hooks/useSubscribe";
import { I18nUtil } from "@utils";

import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";

const I18n = I18nUtil.createScoped(Translations);

interface UseSubscribeProps {
  closePending?: () => void;
}

export const useSubscribe = (props?: UseSubscribeProps) => {
  const { closePending } = props || {};
  const { notifyApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();
  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: bindToken, isPending: bindTokenLoading } = useMutation({
    mutationFn: async ({
      token,
      utc_offset_minutes,
    }: {
      token: string;
      utc_offset_minutes?: number;
    }) => {
      try {
        log.debug("[文案]bind token===", token);
        const response = await notifyApi?.device.bindDevice({
          signature: "",
          token,
          utc_offset_minutes,
        });
        return response;
      } catch (err) {
        log.error("[文案]bind error");
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.BIND_DEVICE],
    onSuccess: (response) => {
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
    },
    onError: (error) => {
      log.error(`[文案]bindToken error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  // 开关状态枚举是OPEN,CLOSED
  // name：agent_agent
  const { mutateAsync: updateSubscription } = useMutation({
    mutationFn: async ({
      status,
      name = "agent_agent",
      utc_offset_minutes,
    }: {
      status: string;
      name?: string;
      utc_offset_minutes?: number;
    }) => {
      try {
        log.debug("[Settings] updateSubscription status", status);
        const response = await notifyApi?.setting.setting({
          name,
          status,
          utc_offset_minutes,
        });
        return response;
      } catch (err) {
        log.error("[Settings] updateSubscription error", err);
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.UPDATE_SUBSCRIPTION],
    onSuccess: (response) => {
      log.debug(
        "[Settings] updateSubscription onSuccess",
        JSON.stringify(response),
      );
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
      if (response?.data?.data) {
        log.debug("[Settings] updateSubscription onSuccess handleSuccess");
        handleSuccess();
      }
    },
    onError: (error) => {
      log.error(`[文案]updateSubscription error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  const { mutateAsync: getPushSetting } = useMutation({
    mutationFn: async () => {
      try {
        log.debug("[文案]getPushSetting");
        const response = await notifyApi?.settings.listSettings();
        return response;
      } catch (err) {
        log.error("[文案]getPushSetting error");
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.GET_PUSH_SETTING],
    onSuccess: (response) => {
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
    },
    onError: (error) => {
      log.error(`[文案]getPushSetting error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  const handleFail = (code?: string) => {
    setLoading(false);
    closePending?.();
    if (code === "1304") {
      OspToast.error({
        content: I18n.t("Oops...Something went wrong. Please try again."),
      });
    } else {
      OspToast.error({
        content: I18n.t("Fail to connect. Please try again."),
      });
    }
  };

  const handleSuccess = () => {
    setTimeout(() => {
      setLoading(false);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SUBSCRIBE_CHECK],
      });
      closePending?.();
    }, 1000);
  };

  return {
    bindToken,
    bindTokenLoading,
    updateSubscription,
    isLoading,
    getPushSetting,
  };
};
